import React, { useState, useEffect } from "react";
import Icon from "@mdi/react";
import {
    mdiCalendarEdit,
    mdiTrashCan,
    mdiMinusCircle,
    mdiCheckCircle,
    mdiUndo,
    mdiAlertCircleOutline,
    mdiCoffeeOutline,
    mdiAirplaneTakeoff,
} from "@mdi/js";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { timeToMinutes, minutesToTime, forwardPack } from "../utils/timeOverlap";
import "./EditSlotsModal.css";

/**
 * EditSlotsModal - Edit a single day's availability (shift + remove), home OR travel
 *
 * Mode-aware: pass `travelPeriod` to edit a travel day (purple accent + travel banner
 * + travelPeriodId in the save payload); omit it for a home day. Travel slots are the
 * same availability_overrides docs, just tagged with travelPeriodId.
 *
 * Opened by clicking an existing slot in the grid. ONE column showing the day's slots:
 *  - Each movable slot has -15/+15 to shift its start, plus a remove toggle.
 *  - A shift shows inline as "9:00 AM → 9:15 AM" with a "moved" tag; a slot pushed by that
 *    shift (cascade, up to a gap) shows a "nudged" tag. Removed rows are struck through.
 *  - Gaps between slots render as grey "Break H - H" rows, so you can SEE where a nudge
 *    stops (a gap / lunch break absorbs the shift).
 *
 * All edits are day-specific — the weekly pattern is never changed. A shift blocks the
 * original time and re-adds the slot at its new time (the original never double-shows).
 * Save posts shifts + removals to PATCH /overrides/nudge via onApply.
 */
const EditSlotsModal = ({
    isOpen,
    date,
    overrideId,
    existingSlots = [],
    dayOfWeek,
    travelPeriod,
    onApply,
    onCancel,
}) => {
    // Travel-aware: when a travelPeriod is passed the modal edits a travel day
    // (purple accent + travel banner + travelPeriodId in the save payload). The
    // slots themselves are the same availability_overrides docs either way.
    const isTravel = !!travelPeriod;
    // Theme accent: travel = the fixed dark travel purple, home = turquoise. NEVER the
    // per-trip travelPeriod.color — that belongs ONLY in the destination pill.
    const accentColor = isTravel ? "var(--dl-color-purple-default)" : "var(--dl-color-turquoise-default)";
    const [slots, setSlots] = useState([]); // the day's slots (original, immutable order/time)
    const [removalIds, setRemovalIds] = useState(() => new Set());
    const [shiftDeltas, setShiftDeltas] = useState({}); // slotId -> minutes shifted
    const [isSaving, setIsSaving] = useState(false);
    const [cascadeConfirmed, setCascadeConfirmed] = useState(false); // OK'd nudging following slots this session
    const [pendingShift, setPendingShift] = useState(null); // { slotId, next } awaiting cascade confirm

    useEffect(() => {
        if (isOpen) {
            const editableSlots = (existingSlots || [])
                .map((slot, idx) => ({
                    id: `existing-${idx}`,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    slotDuration: slot.duration || slot.slotDuration || 60,
                    source: slot.source || "recurring",
                    isBooked: slot.isBooked || false,
                    isExisting: true,
                }))
                .sort((a, b) => a.startTime.localeCompare(b.startTime));
            setSlots(editableSlots);
            setRemovalIds(new Set());
            setShiftDeltas({});
            setIsSaving(false);
            setCascadeConfirmed(false);
            setPendingShift(null);
        }
    }, [isOpen, existingSlots]);

    const wouldCascade = deltas => {
        const input = slots.map(s => ({
            id: s.id,
            startTime: minutesToTime(timeToMinutes(s.startTime) + (deltas[s.id] || 0)),
            slotDuration: s.slotDuration,
            isBooked: s.isBooked,
            source: s.source,
            removed: removalIds.has(s.id),
        }));
        const desiredById = Object.fromEntries(input.map(i => [i.id, i.startTime]));
        const { placements } = forwardPack(input);
        return placements.some(p => timeToMinutes(p.startTime) > timeToMinutes(desiredById[p.id]));
    };

    // "Up to next gap": apply the shift to the pressed slot only; forwardPack cascades the
    // following slots but stops at the first gap that absorbs the shift.
    const confirmCascade = () => {
        if (pendingShift) {
            setShiftDeltas(prev => ({ ...prev, [pendingShift.slotId]: pendingShift.next }));
            setCascadeConfirmed(true);
        }
        setPendingShift(null);
    };
    // "Shift the rest of the day": pre-shift the pressed slot AND every slot after it by the
    // same amount, so gaps (lunch, breaks) are preserved and everything moves together.
    const applyWholeDay = () => {
        if (pendingShift) {
            const inc = pendingShift.next - (shiftDeltas[pendingShift.slotId] || 0);
            const pressedStart = timeToMinutes(origById[pendingShift.slotId].startTime);
            setShiftDeltas(prev => {
                const nextDeltas = { ...prev, [pendingShift.slotId]: pendingShift.next };
                slots.forEach(s => {
                    if (s.id !== pendingShift.slotId && timeToMinutes(s.startTime) > pressedStart) {
                        nextDeltas[s.id] = (prev[s.id] || 0) + inc;
                    }
                });
                return nextDeltas;
            });
            setCascadeConfirmed(true);
        }
        setPendingShift(null);
    };
    const cancelCascade = () => setPendingShift(null);

    const canDeleteSlot = slot => {
        if (slot.isBooked) return false;
        if (slot.source === "lunch") return false;
        return slot.source === "recurring" || slot.source === "override";
    };
    const canShiftSlot = slot => canDeleteSlot(slot);

    const toggleRemove = slot => {
        if (!canDeleteSlot(slot)) {
            toast.error("Cannot delete this slot");
            return;
        }
        setRemovalIds(prev => {
            const next = new Set(prev);
            if (next.has(slot.id)) next.delete(slot.id);
            else next.add(slot.id);
            return next;
        });
    };

    // Shift a slot's start by ±minutes (clamped). If it would push the following slots and
    // the user hasn't OK'd that this session, ask first.
    const shiftSlot = (slot, delta) => {
        if (!canShiftSlot(slot)) return;
        const origStart = timeToMinutes(slot.startTime);
        const minDelta = -origStart;
        const maxDelta = 24 * 60 - slot.slotDuration - origStart;
        const next = Math.max(minDelta, Math.min((shiftDeltas[slot.id] || 0) + delta, maxDelta));
        if (next === (shiftDeltas[slot.id] || 0)) return;

        const prospective = { ...shiftDeltas, [slot.id]: next };
        if (!cascadeConfirmed && wouldCascade(prospective)) {
            setPendingShift({ slotId: slot.id, next });
            return;
        }
        setShiftDeltas(prospective);
    };

    // Cascade preview: pack the working (shifted, non-removed) slots forward, up to a gap.
    const packInput = slots.map(s => ({
        id: s.id,
        startTime: minutesToTime(timeToMinutes(s.startTime) + (shiftDeltas[s.id] || 0)),
        slotDuration: s.slotDuration,
        isBooked: s.isBooked,
        source: s.source,
        removed: removalIds.has(s.id),
    }));
    const { placements, blockedReason } = forwardPack(packInput);
    const origById = Object.fromEntries(slots.map(s => [s.id, s]));
    const placementById = Object.fromEntries(placements.map(p => [p.id, p]));

    // Build the display list: slot rows in chronological order, with a grey "Break" row
    // wherever there's a genuine gap. A removed slot keeps its ORIGINAL position (struck
    // in place) so the break never jumps above it; active slots use their post-shift time.
    const displayRows = [];
    let prevEnd = null;
    slots.forEach(slot => {
        const removed = removalIds.has(slot.id);
        const final = placementById[slot.id];
        const startMin = !removed && final ? timeToMinutes(final.startTime) : timeToMinutes(slot.startTime);
        const endMin = !removed && final ? timeToMinutes(final.endTime) : timeToMinutes(slot.endTime);
        if (prevEnd != null && startMin > prevEnd) {
            displayRows.push({
                type: "break",
                key: `break-${slot.id}`,
                start: minutesToTime(prevEnd),
                end: minutesToTime(startMin),
            });
        }
        displayRows.push({ type: "slot", key: slot.id, slot, removed, final });
        prevEnd = prevEnd == null ? endMin : Math.max(prevEnd, endMin);
    });

    // Derive the operations to send
    const shiftOps = placements
        .filter(p => origById[p.id] && p.startTime !== origById[p.id].startTime)
        .map(p => ({
            oldStartTime: origById[p.id].startTime,
            newStartTime: p.startTime,
            newEndTime: p.endTime,
            slotDuration: p.slotDuration,
        }));
    const removedRecurring = slots.filter(s => removalIds.has(s.id) && s.source === "recurring").map(s => s.startTime);
    const removedAdhoc = slots.filter(s => removalIds.has(s.id) && s.source === "override").map(s => s.startTime);
    const hasChanges = shiftOps.length > 0 || removedRecurring.length > 0 || removedAdhoc.length > 0;

    const blockedMessage = reason => {
        if (!reason) return null;
        const t = reason.time ? formatTimeDisplay(reason.time) : "";
        switch (reason.type) {
            case "booked":
                return `A booking at ${t} is in the way. Cancel it in your Bookings dashboard first.`;
            case "lunch":
                return "This would run into your lunch break. Shift by less, or adjust the slots.";
            case "endofday":
                return "There isn't enough room later that day. Shift by less.";
            default:
                return null;
        }
    };

    const handleApply = async () => {
        if (blockedReason) {
            toast.error(blockedMessage(blockedReason));
            return;
        }
        if (!hasChanges) {
            toast.info("No changes to save");
            return;
        }
        setIsSaving(true);
        try {
            await onApply({
                date,
                shifts: shiftOps,
                blockedSlots: removedRecurring,
                removedSlots: removedAdhoc,
                travelPeriodId: travelPeriod?.id || null,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const renderSlotRow = ({ slot, removed, final }) => {
        const delta = shiftDeltas[slot.id] || 0;
        const moved = !removed && delta !== 0;
        const nudged = !removed && delta === 0 && final && final.startTime !== slot.startTime;
        const changed = (moved || nudged) && final;
        return (
            <div key={slot.id} className={`accumulated-slot-item${removed ? " marked-for-removal" : ""}`}>
                <div className="slot-info">
                    <div className="slot-time-text">
                        {changed ? (
                            <>
                                {formatTimeDisplay(slot.startTime)}{" "}
                                <span className="slot-shift-target">→ {formatTimeDisplay(final.startTime)}</span>
                            </>
                        ) : (
                            <>
                                {formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}
                            </>
                        )}
                        {moved && <span className="row-tag row-tag-moved">moved</span>}
                        {nudged && <span className="row-tag row-tag-nudged">nudged</span>}
                    </div>
                    <SourceBadge source={slot.source} isBooked={slot.isBooked} isTravel={isTravel} />
                </div>
                <div className="slot-row-controls">
                    {!removed && canShiftSlot(slot) && (
                        <div className="slot-nudge">
                            <button type="button" onClick={() => shiftSlot(slot, -15)} title="Shift 15 min earlier">
                                -15
                            </button>
                            <button type="button" onClick={() => shiftSlot(slot, 15)} title="Shift 15 min later">
                                +15
                            </button>
                        </div>
                    )}
                    {canDeleteSlot(slot) && (
                        <button
                            className="btn-delete-slot"
                            onClick={() => toggleRemove(slot)}
                            title={
                                removed
                                    ? "Undo remove"
                                    : slot.source === "recurring"
                                      ? "Remove this slot (this date only)"
                                      : "Remove this ad-hoc slot"
                            }>
                            <Icon
                                path={removed ? mdiUndo : slot.source === "recurring" ? mdiMinusCircle : mdiTrashCan}
                                size={0.8}
                            />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    if (!isOpen || !date) return null;

    const luxonDate = DateTime.fromISO(date);

    return (
        <div className="edit-home-slots-overlay">
            <div
                className={`edit-home-slots-modal edit-home-slots-modal--single${isTravel ? " edit-home-slots-modal--travel" : ""}`}>
                {/* WHITE HEADER */}
                <div className="edit-home-slots-header">
                    <div className="edit-home-slots-header-content">
                        <Icon path={isTravel ? mdiAirplaneTakeoff : mdiCalendarEdit} size={1.2} color={accentColor} />
                        <div>
                            <h2 style={{ color: accentColor }}>
                                {isTravel ? "Edit Travel Availability" : "Edit Home Availability"}
                            </h2>
                            <p className="date-subtitle">{luxonDate.toFormat("EEEE, MMMM d, yyyy")}</p>
                        </div>
                    </div>
                    <button className="edit-home-slots-close" onClick={onCancel}>
                        ×
                    </button>
                </div>

                {/* Travel context banner (travel mode only) */}
                {isTravel && (
                    <div className="travel-context-banner">
                        <div
                            className="travel-destination"
                            style={{
                                borderColor: `${travelPeriod.color}80`,
                                backgroundColor: `${travelPeriod.color}15`,
                            }}>
                            <Icon path={mdiAirplaneTakeoff} size={0.7} style={{ color: travelPeriod.color }} />
                            <span>{travelPeriod.destination}</span>
                        </div>
                        {travelPeriod.timezoneAbbr && (
                            <div className="travel-timezone">
                                {travelPeriod.timezoneAbbr} (UTC{travelPeriod.timezoneOffset})
                            </div>
                        )}
                    </div>
                )}

                {/* Cascade confirm — a shift ran into the next slot (amber caution) */}
                {pendingShift && (
                    <div className="edit-home-cascade-confirm">
                        <div className="edit-home-cascade-main">
                            <Icon path={mdiAlertCircleOutline} size={0.85} color="var(--dl-color-orange-default)" />
                            <div className="edit-home-cascade-body">
                                <strong>This runs into the next slot. Shift the following slots?</strong>
                                <p>
                                    <b>Up to next break</b>: stops when a break (like lunch) absorbs the shifted slot.{" "}
                                    <b>Rest of day</b>: moves all following slots by the same amount, keeping the break
                                    duration intact.
                                </p>
                            </div>
                        </div>
                        <div className="edit-home-cascade-actions">
                            <button type="button" className="btn-secondary" onClick={cancelCascade}>
                                Cancel
                            </button>
                            <button type="button" className="btn-primary cascade-yes" onClick={confirmCascade}>
                                Up to next break
                            </button>
                            <button type="button" className="btn-primary cascade-yes" onClick={applyWholeDay}>
                                Rest of day
                            </button>
                        </div>
                    </div>
                )}

                {/* BODY - single column */}
                <div className="edit-home-slots-body edit-home-slots-body--single">
                    <h3>This day's slots</h3>
                    <p className="edit-home-day-hint">
                        {isTravel
                            ? "Shift or remove times for this travel day only."
                            : "Shift or remove times for this date only. Your weekly pattern stays unchanged."}
                    </p>
                    {slots.length > 0 ? (
                        <div className="accumulated-slots-summary">
                            <div className="summary-header">
                                <Icon path={mdiCheckCircle} size={0.8} color={accentColor} />
                                <span>
                                    {placements.length} slot{placements.length !== 1 ? "s" : ""}
                                </span>
                            </div>
                            {blockedReason && (
                                <div className="edit-home-blocked">
                                    <Icon path={mdiAlertCircleOutline} size={0.8} color="var(--dl-color-danger)" />
                                    <span>{blockedMessage(blockedReason)}</span>
                                </div>
                            )}
                            <div className="accumulated-slots-list">
                                {displayRows.map(row =>
                                    row.type === "break" ? (
                                        <div key={row.key} className="edit-day-break">
                                            <Icon path={mdiCoffeeOutline} size={0.6} />
                                            <span>
                                                Break {formatTimeDisplay(row.start)} - {formatTimeDisplay(row.end)}
                                            </span>
                                        </div>
                                    ) : (
                                        renderSlotRow(row)
                                    )
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="slots-column-empty">No availability on this day.</div>
                    )}
                </div>

                {/* WHITE FOOTER */}
                <div className="edit-home-slots-footer">
                    <button className="btn-secondary" onClick={onCancel} disabled={isSaving}>
                        Cancel
                    </button>
                    <button
                        className="btn-primary home-context"
                        onClick={handleApply}
                        disabled={isSaving || !hasChanges || !!blockedReason}>
                        {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// SOURCE BADGE COMPONENT
// ============================================

const getSourceTooltip = (source, isBooked, isTravel) => {
    if (isBooked) return "Student has booked this time. Cancel booking first to delete.";
    switch (source) {
        case "recurring":
            return "From your weekly schedule. Removing it affects this date only; your weekly pattern is unchanged.";
        // On a travel day there is no weekly pattern — an override slot IS the trip's
        // schedule, not an ad-hoc adjustment on top of a home pattern.
        case "override":
            return isTravel
                ? "A slot for this travel day. Removing it deletes it from this trip."
                : "Extra time added just for today. Removing it deletes today's addition.";
        case "lunch":
            return "Lunch break. Edit weekly schedule to change lunch times.";
        default:
            return "";
    }
};

const SourceBadge = ({ source, isBooked, isTravel }) => {
    const badgeConfig = {
        recurring: {
            bg: "color-mix(in srgb, var(--dl-color-turquoise-default) 15%, transparent)",
            text: "Weekly Schedule",
            color: "var(--dl-color-turquoise-900)",
            border: "1px solid color-mix(in srgb, var(--dl-color-turquoise-default) 30%, transparent)",
        },
        // In travel mode, override slots ARE the schedule → "Travel Slot", not "Ad-Hoc Adjustment".
        override: isTravel
            ? {
                  bg: "color-mix(in srgb, var(--dl-color-purple-900) 15%, transparent)",
                  text: "Travel Slot",
                  color: "var(--dl-color-purple-default)",
                  border: "1px solid color-mix(in srgb, var(--dl-color-purple-900) 30%, transparent)",
              }
            : {
                  bg: "color-mix(in srgb, var(--dl-color-purple-900) 15%, transparent)",
                  text: "Ad-Hoc Adjustment",
                  color: "#7c3aed",
                  border: "1px solid color-mix(in srgb, var(--dl-color-purple-900) 30%, transparent)",
              },
        lunch: { bg: "#bdbdbd", text: "Lunch Break", color: "#333" },
    };

    if (isBooked) {
        return (
            <span
                style={{
                    backgroundColor: "#fee2e2",
                    color: "#991b1b",
                    border: "1px solid #ef4444",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "11px",
                    fontWeight: 600,
                    cursor: "help",
                }}
                title={getSourceTooltip(source, isBooked, isTravel)}>
                BOOKED
            </span>
        );
    }

    const style = badgeConfig[source] || badgeConfig.recurring;
    return (
        <span
            style={{
                backgroundColor: style.bg,
                color: style.color,
                border: style.border || "none",
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: 500,
                cursor: "help",
            }}
            title={getSourceTooltip(source, isBooked, isTravel)}>
            {style.text}
        </span>
    );
};

// ============================================
// HELPER
// ============================================

const formatTimeDisplay = timeString => {
    const [hour, minute] = timeString.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${String(minute).padStart(2, "0")} ${period}`;
};

export default EditSlotsModal;
